/**
 * Quem é o produto e quem o entrega — as duas coisas, num lugar só.
 *
 * **Por que isto é um arquivo e não texto solto nas telas.** O Cockpit é multi-inquilino e
 * vendido por um parceiro: a concessionária é o *cliente*, a IA Solutions é quem *entrega*,
 * e o produto é de terceiro em relação às duas. São três identidades distintas, e cada tela
 * que escrevesse uma delas à mão seria uma chance de trocá-las. A do cliente já vem do
 * servidor (`quem.organizacao_nome`, no topo); as outras duas moram aqui.
 *
 * E há a razão comercial: o dia em que um segundo parceiro revender esta plataforma, a
 * troca precisa ser uma edição neste arquivo — não uma varredura atrás de "IA Solutions"
 * espalhado por seis componentes.
 *
 * **Onde cada uma aparece** (a decisão de produto, escrita para não se perder):
 *
 * - *Topo da tela*: a **concessionária**. É o cockpit dela, e quem está olhando precisa
 *   saber de quem é o dado que está na tela — é a mesma pergunta que o RLS responde no
 *   banco.
 * - *Barra lateral e entrada*: o **produto**. É o que a pessoa abriu.
 * - *Rodapé e entrada*: o **fornecedor**, discreto e presente. Numa sala onde o diretor
 *   pergunta "quem é que faz isso?", a resposta não pode depender de alguém lembrar.
 *
 * **Sobre o desenho da marca.** A assinatura é tipográfica de propósito: não há arquivo de
 * logotipo da IA Solutions neste repositório, e desenhar um parecido seria inventar a marca
 * de uma empresa de verdade. Quando o SVG oficial existir, ele entra aqui — e só aqui.
 */
import { Icone } from "./icones";

/** A empresa que entrega a solução. Trocar de parceiro é trocar estas quatro linhas. */
export const FORNECEDOR = {
  nome: "IA Solutions",
  //: A parte que distingue a razão social, em caixa alta na assinatura.
  sufixo: "MNG",
  //: A frase que a própria empresa usa. Curta o bastante para caber num rodapé.
  promessa: "Menos hype. Mais ROI.",
  site: "https://iasolutionsmng.tech",
} as const;

/** O nome do produto. Ele não é da concessionária nem do fornecedor. */
export const PRODUTO = { nome: "Cockpit", complemento: "Rodovias" } as const;

/**
 * A marca do produto, com o alvo à esquerda.
 *
 * `destaque` é a variante da tela de entrada, onde ela é o título da página; sem ele, é o
 * cabeçalho da barra lateral.
 */
export function MarcaDoProduto({ destaque = false }: { destaque?: boolean }) {
  return (
    <span
      className={`flex items-center gap-2 font-extrabold text-tinta ${
        destaque ? "text-[18px]" : ""
      }`}
    >
      <span className="text-azul">
        <Icone.Alvo />
      </span>
      {PRODUTO.nome}{" "}
      <em className="text-azul not-italic">{PRODUTO.complemento}</em>
    </span>
  );
}

/**
 * A assinatura de quem entrega.
 *
 * `com_promessa` só na tela de entrada: ali há espaço e é o único momento em que alguém
 * lê o rodapé. Dentro do cockpit a frase vira ruído sobre uma tela de trabalho, e a
 * assinatura sozinha já responde a pergunta.
 */
export function AssinaturaDoFornecedor({
  com_promessa = false,
}: {
  com_promessa?: boolean;
}) {
  return (
    <span className="grid gap-0.5 leading-tight">
      <span className="flex items-baseline gap-1 text-[10.5px] text-tinta-3">
        <span>Entregue por</span>
        <a
          href={FORNECEDOR.site}
          target="_blank"
          rel="noreferrer"
          className="font-bold tracking-[0.04em] text-tinta-2 uppercase hover:text-azul-claro"
        >
          {FORNECEDOR.nome}{" "}
          <span className="text-azul">{FORNECEDOR.sufixo}</span>
        </a>
      </span>
      {com_promessa && (
        <span className="text-[10px] text-tinta-3">{FORNECEDOR.promessa}</span>
      )}
    </span>
  );
}
